/**
 * M11: PixiJS textures for the shared art (ships, stars, glows, rings), baked ONCE per renderer into
 * reusable textures — nothing here is redrawn per frame. The map and the battle scenes only create
 * Sprites that share these textures.
 *
 * Everything is procedural (no external image files, no licence questions).
 */
import * as PIXI from 'pixi.js';
import type { StarType } from '@galaxis/shared';
import { ART_BOX, designOf, shipPalette, toneColor, visibleDamageSpots, type Layer, type ShipClass, type Tone } from './ship-art.ts';
import { mixHex, starStyle, type Role } from '../design/tokens.ts';
import type { Quality } from './quality.ts';

/** Pixels per art unit when a ship is baked. */
const SHIP_PX = 32;
/** Radius in texture pixels of every star texture (box = 2 × this). */
export const STAR_TEX_R = 64;

const shade = (base: number): PIXI.FillGradient => new PIXI.FillGradient({
  type: 'linear',
  start: { x: 0, y: 0 }, end: { x: 1, y: 1 },
  colorStops: [{ offset: 0, color: mixHex(base, 0xffffff, 0.18) }, { offset: 1, color: mixHex(base, 0x000000, 0.28) }],
  textureSpace: 'local',
});

function drawLayer(g: PIXI.Graphics, l: Layer, pal: ReturnType<typeof shipPalette>, grad: Partial<Record<Tone, PIXI.FillGradient>>): void {
  const S = SHIP_PX;
  const fill = (t: Tone, alpha = 1): PIXI.FillInput => (grad[t] ? grad[t]! : { color: toneColor(pal, t), alpha });
  switch (l.kind) {
    case 'poly': {
      const pts = l.pts.map(v => v * S);
      g.poly(pts).fill(l.alpha !== undefined && !grad[l.fill] ? { color: toneColor(pal, l.fill), alpha: l.alpha } : fill(l.fill));
      if (l.stroke) g.poly(pts).stroke({ color: toneColor(pal, l.stroke), width: 0.02 * S, alpha: 0.9, join: 'round' });
      break;
    }
    case 'circle':
      g.circle(l.cx * S, l.cy * S, l.r * S).fill({ color: toneColor(pal, l.fill) });
      if (l.stroke) g.circle(l.cx * S, l.cy * S, l.r * S).stroke({ color: toneColor(pal, l.stroke), width: 0.02 * S });
      break;
    case 'ellipse':
      g.ellipse(l.cx * S, l.cy * S, l.rx * S, l.ry * S).fill({ color: toneColor(pal, l.fill) });
      break;
    case 'line':
      g.poly(l.pts.map(v => v * S), false).stroke({ color: toneColor(pal, l.stroke), width: l.width * S, cap: 'round', join: 'round' });
      break;
  }
}

export class ArtAtlas {
  private cache = new Map<string, PIXI.Texture>();
  private resolution: number;

  constructor(private renderer: PIXI.Renderer, quality: Quality) {
    this.resolution = quality === 'low' ? 1 : 2;
  }

  private bake(key: string, build: () => PIXI.Graphics): PIXI.Texture {
    let t = this.cache.get(key);
    if (!t) {
      const g = build();
      t = this.renderer.generateTexture({ target: g, resolution: this.resolution, antialias: true });
      g.destroy();
      this.cache.set(key, t);
    }
    return t;
  }

  /** Ship silhouette incl. a faint engine glow; art origin (0,0) is the texture centre → anchor (0.5, 0.5). */
  ship(cls: ShipClass | string, role: Role, damage = 0): PIXI.Texture {
    const design = designOf(cls);
    const bucket = damage <= 0.2 ? 0 : damage <= 0.5 ? 1 : damage <= 0.8 ? 2 : 3;
    return this.bake(`ship:${design.cls}:${role}:${bucket}`, () => {
      const S = SHIP_PX;
      const g = new PIXI.Graphics();
      // fixes the bounds to the art box so every ship shares the same anchor
      g.rect(ART_BOX.x * S, ART_BOX.y * S, ART_BOX.w * S, ART_BOX.h * S).fill({ color: 0x000000, alpha: 0.001 });
      const pal = shipPalette(role);
      const grad: Partial<Record<Tone, PIXI.FillGradient>> = { hull: shade(pal.hull), hullLight: shade(pal.hullLight), hullDark: shade(pal.hullDark) };
      for (const n of design.nozzles) {
        g.ellipse(n.x * S, (n.y + 0.06) * S, n.w * 1.5 * S, 0.12 * S).fill({ color: design.flame.color, alpha: 0.35 });
        g.ellipse(n.x * S, (n.y + 0.04) * S, n.w * 0.8 * S, 0.06 * S).fill({ color: design.flame.core, alpha: 0.7 });
      }
      for (const l of design.layers) drawLayer(g, l, pal, grad);
      const spots = visibleDamageSpots(bucket === 0 ? 0 : bucket === 1 ? 0.35 : bucket === 2 ? 0.65 : 0.95, design.damageSpots.length);
      design.damageSpots.slice(0, spots).forEach((s, i) => {
        g.circle(s.x * S, s.y * S, s.r * S).fill({ color: pal.burn, alpha: 0.62 });
        if (bucket >= 2) g.circle(s.x * S, s.y * S, s.r * 0.4 * S).fill({ color: pal.ember, alpha: 0.85 });
        if (bucket >= 1) g.poly([(s.x - s.r) * S, (s.y - s.r * 0.4) * S, s.x * S, s.y * S, (s.x + s.r * 0.9) * S, (s.y + s.r) * S], false).stroke({ color: pal.burn, width: 0.025 * S });
        void i;
      });
      return g;
    });
  }

  /** Soft radial glow (white-ish; tint with the sprite). Radius in texture px. */
  glow(radius = 64, steps = 28): PIXI.Texture {
    return this.bake(`glow:${radius}:${steps}`, () => {
      const g = new PIXI.Graphics();
      g.circle(0, 0, radius).fill({ color: 0xffffff, alpha: 0.001 });
      for (let i = steps; i >= 1; i--) {
        const t = i / steps; // 1 outer … small inner
        g.circle(0, 0, radius * t).fill({ color: 0xffffff, alpha: 0.9 / steps * (1 + (1 - t) * 1.6) * (1 - t * 0.35) });
      }
      return g;
    });
  }

  /** Flame / exhaust blob pointing down (+y), tint with the sprite. 64×128 px. */
  flame(): PIXI.Texture {
    return this.bake('flame', () => {
      const g = new PIXI.Graphics();
      g.rect(-32, 0, 64, 128).fill({ color: 0x000000, alpha: 0.001 });
      for (let i = 14; i >= 1; i--) {
        const t = i / 14;
        g.ellipse(0, 128 * t * 0.5, 30 * t, 64 * t).fill({ color: 0xffffff, alpha: 0.07 + (1 - t) * 0.12 });
      }
      return g;
    });
  }

  /** Thin ring with a soft outer glow (selection, ownership). Radius 64 px. */
  ring(): PIXI.Texture {
    return this.bake('ring', () => {
      const g = new PIXI.Graphics();
      g.circle(0, 0, 68).fill({ color: 0x000000, alpha: 0.001 });
      g.circle(0, 0, 62).stroke({ color: 0xffffff, width: 7, alpha: 0.12 });
      g.circle(0, 0, 62).stroke({ color: 0xffffff, width: 3, alpha: 0.35 });
      g.circle(0, 0, 62).stroke({ color: 0xffffff, width: 1.2, alpha: 0.95 });
      return g;
    });
  }

  /** Four corner brackets (selected system), 128×128 px. */
  brackets(): PIXI.Texture {
    return this.bake('brackets', () => {
      const g = new PIXI.Graphics();
      g.rect(-64, -64, 128, 128).fill({ color: 0x000000, alpha: 0.001 });
      const c = 26, o = 58;
      for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        g.poly([sx * o, sy * (o - c), sx * o, sy * o, sx * (o - c), sy * o], false).stroke({ color: 0xffffff, width: 5, alpha: 0.18, join: 'round' });
        g.poly([sx * o, sy * (o - c), sx * o, sy * o, sx * (o - c), sy * o], false).stroke({ color: 0xffffff, width: 2, alpha: 0.95, join: 'round' });
      }
      return g;
    });
  }

  /** The dim dot standing for a system the player only knows the position of. */
  dot(): PIXI.Texture {
    return this.bake('dot', () => {
      const g = new PIXI.Graphics();
      g.circle(0, 0, 8).fill({ color: 0xffffff, alpha: 0.001 });
      g.circle(0, 0, 4).fill({ color: 0xffffff, alpha: 0.9 });
      return g;
    });
  }

  /** A hexagon-free soft territory disc (radial, flat centre), 64 px radius. */
  territory(): PIXI.Texture {
    return this.bake('territory', () => {
      const g = new PIXI.Graphics();
      g.circle(0, 0, 64).fill({ color: 0xffffff, alpha: 0.001 });
      for (let i = 22; i >= 1; i--) {
        const t = i / 22;
        g.circle(0, 0, 64 * t).fill({ color: 0xffffff, alpha: 0.06 * (1 - t * 0.55) });
      }
      return g;
    });
  }

  /** Star by type: glow + disc + type-specific features. The glow reaches STAR_TEX_R. */
  star(type: StarType | string): PIXI.Texture {
    const st = starStyle(type);
    return this.bake(`star:${type}`, () => {
      const R = STAR_TEX_R;
      const g = new PIXI.Graphics();
      g.circle(0, 0, R).fill({ color: 0x000000, alpha: 0.001 });
      const disc = R / st.glowScale; // radius of the disc in texture px
      const glow = (color: number, from: number, aMul = 1) => {
        for (let i = 18; i >= 1; i--) {
          const t = i / 18;
          g.circle(0, 0, from + (R - from) * t).fill({ color, alpha: 0.07 * aMul * (1 - t * 0.7) });
        }
      };
      switch (type) {
        case 'black_hole': {
          glow(st.glow, disc * 1.2, 0.55);
          // accretion disk: a tilted stack of thin ellipses, hot inner edge
          for (let i = 0; i < 9; i++) {
            const k = i / 8;
            const rx = disc * (1.5 + k * 1.7), ry = rx * 0.22;
            g.ellipse(0, 0, rx, ry).stroke({ color: mixHex(0xffffff, st.glow, k), width: 2.4 - k * 1.2, alpha: 0.55 - k * 0.35 });
          }
          g.circle(0, 0, disc).fill({ color: 0x000000 });
          g.circle(0, 0, disc * 1.04).stroke({ color: 0xffe2b0, width: 1.6, alpha: 0.85 }); // lensed photon ring
          g.ellipse(0, -disc * 0.55, disc * 1.35, disc * 0.2).stroke({ color: 0xffd9a0, width: 1.4, alpha: 0.55 }); // light bent over the top
          break;
        }
        case 'neutron_star': {
          glow(st.glow, disc, 0.9);
          g.poly([0, -R, disc * 0.35, 0, 0, R, -disc * 0.35, 0]).fill({ color: st.glow, alpha: 0.28 }); // polar jets
          g.poly([-R * 0.45, 0, 0, disc * 0.22, R * 0.45, 0, 0, -disc * 0.22]).fill({ color: st.glow, alpha: 0.14 });
          g.circle(0, 0, disc * 1.5).fill({ color: st.glow, alpha: 0.35 });
          g.circle(0, 0, disc).fill({ color: st.core });
          break;
        }
        case 'binary': {
          glow(st.glow, disc * 0.8, 1.1);
          const d = disc * 1.05;
          g.circle(-d, 0, disc * 0.82).fill({ color: 0x9fd8ff, alpha: 0.35 });
          g.circle(d, disc * 0.1, disc * 0.7).fill({ color: 0xffa24d, alpha: 0.35 });
          g.circle(-d, 0, disc * 0.62).fill({ color: 0xeaf7ff });
          g.circle(d, disc * 0.1, disc * 0.52).fill({ color: 0xffd9a1 });
          break;
        }
        case 'blue_giant': {
          glow(st.glow, disc, 1.2);
          for (let a = 0; a < 4; a++) { // diffraction spikes
            const ang = (a * Math.PI) / 4 + Math.PI / 8;
            g.poly([Math.cos(ang) * R, Math.sin(ang) * R, Math.cos(ang + Math.PI / 2) * disc * 0.25, Math.sin(ang + Math.PI / 2) * disc * 0.25, -Math.cos(ang) * R, -Math.sin(ang) * R, Math.cos(ang - Math.PI / 2) * disc * 0.25, Math.sin(ang - Math.PI / 2) * disc * 0.25]).fill({ color: st.glow, alpha: 0.14 });
          }
          g.circle(0, 0, disc * 1.25).fill({ color: st.glow, alpha: 0.4 });
          g.circle(0, 0, disc).fill({ color: st.core });
          break;
        }
        case 'red_dwarf': {
          glow(st.glow, disc, 0.8);
          g.circle(0, 0, disc * 1.2).fill({ color: st.glow, alpha: 0.3 });
          g.circle(0, 0, disc).fill({ color: mixHex(st.core, st.glow, 0.45) });
          g.circle(disc * 0.35, -disc * 0.3, disc * 0.28).fill({ color: 0xffd0c8, alpha: 0.5 });
          break;
        }
        case 'white_dwarf': {
          glow(st.glow, disc, 0.9);
          g.poly([-R * 0.7, 0, 0, disc * 0.14, R * 0.7, 0, 0, -disc * 0.14]).fill({ color: 0xffffff, alpha: 0.2 });
          g.poly([0, -R * 0.7, disc * 0.14, 0, 0, R * 0.7, -disc * 0.14, 0]).fill({ color: 0xffffff, alpha: 0.2 });
          g.circle(0, 0, disc * 1.3).fill({ color: st.glow, alpha: 0.4 });
          g.circle(0, 0, disc).fill({ color: st.core });
          break;
        }
        default: { // yellow dwarf and anything unknown
          glow(st.glow, disc, 1);
          for (let a = 0; a < 8; a++) { // corona rays
            const ang = (a * Math.PI) / 4;
            g.poly([Math.cos(ang) * disc * 1.1, Math.sin(ang) * disc * 1.1, Math.cos(ang + 0.12) * R * 0.78, Math.sin(ang + 0.12) * R * 0.78, Math.cos(ang - 0.12) * R * 0.78, Math.sin(ang - 0.12) * R * 0.78]).fill({ color: st.glow, alpha: 0.09 });
          }
          g.circle(0, 0, disc * 1.25).fill({ color: st.glow, alpha: 0.4 });
          g.circle(0, 0, disc).fill({ color: mixHex(st.core, st.glow, 0.25) });
          g.circle(-disc * 0.2, -disc * 0.2, disc * 0.55).fill({ color: st.core, alpha: 0.9 });
        }
      }
      return g;
    });
  }

  /** Number of baked textures (diagnostics / tests). */
  get size(): number { return this.cache.size; }

  destroy(): void {
    for (const t of this.cache.values()) t.destroy(true);
    this.cache.clear();
  }
}
